import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { PublicShell } from "@/auth/PublicShell";
import { TOKEN_ERRORS, humanise, verifyEmail } from "@/auth/public_api";

/** Email verification — the one page nobody fills in.
 *
 *  The link IS the action, so this runs on mount and the person only reads the
 *  outcome. Two details that are easy to get wrong:
 *
 *  1. **It must run exactly once.** React 18 StrictMode mounts, unmounts and
 *     remounts every component in development. A verification token is
 *     SINGLE-USE, so a naive effect spends it on the first mount and then
 *     reports TOKEN_INVALID on the second — a bug that appears only in dev and
 *     looks exactly like a broken token. The ref guard is why that does not
 *     happen. (It is also correct in production: a re-render must not re-spend
 *     the token.)
 *  2. **A failure here is not the end of the road.** An expired verification
 *     link is recoverable by signing in, which re-sends it — so the error state
 *     offers that rather than a dead end.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"working" | "ok" | "failed">("working");
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;      // StrictMode double-mount would spend the token twice
    ran.current = true;
    verifyEmail(token)
      .then(() => setState("ok"))
      .catch((err) => {
        const e = err as { code: string; message?: string };
        setError(humanise(e.code, e.message, TOKEN_ERRORS));
        setState("failed");
      });
  }, [token]);

  if (state === "working") {
    return (
      <PublicShell eyebrow="Orbit Platform" title="Verifying your email" sub="One moment.">
        <div className="cap" style={{ textAlign: "center" }}>Checking the link…</div>
      </PublicShell>
    );
  }

  if (state === "ok") {
    return (
      <PublicShell
        eyebrow="Orbit Platform"
        title="Email verified"
        sub="Your address is confirmed. You can sign in now."
      >
        <Link to="/" className="btn-signin" style={{ textAlign: "center", display: "block" }}>
          Sign in →
        </Link>
      </PublicShell>
    );
  }

  return (
    <PublicShell eyebrow="Orbit Platform" title="That link didn't work" sub={error}>
      <div className="cap" style={{ textAlign: "center", marginBottom: 14 }}>
        Signing in sends a fresh verification email, so this is recoverable without
        an administrator.
      </div>
      <Link to="/" className="btn-signin" style={{ textAlign: "center", display: "block" }}>
        Go to sign in →
      </Link>
    </PublicShell>
  );
}
